import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { PresenceBar } from "@/components/PresenceBar";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Database,
  GitBranch,
  BarChart3,
  Menu,
  LogOut,
  X,
  CalendarDays,
  CalendarClock,
  CalendarCheck,
  ClipboardCheck,
  Shield,
  ChevronLeft,
  FileSpreadsheet,
  Pill,
  Sun,
  Moon,
  Cog,
  Activity,
  Zap,
  ChevronUp,
  ChevronDown,
  TrendingUp,
  Map,
  Stethoscope
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
{/* Logo removed per branding requirements */}

// Navigation items with role restrictions
// managerOnly: true = only visible to manager/admin
// repVisible: true = visible to reps (when not present, assumes visible to all)
const pipelineItems = [
  { path: "/referrals?type=ss", icon: Activity, label: "Sleep Study (SS)", testId: "nav-pipeline-ss", pipeline: "ss" },
  { path: "/referrals?type=tt", icon: Zap, label: "Titration (TT)", testId: "nav-pipeline-tt", pipeline: "tt" },
];

const repsItems = [
  { grouped: true, path: "/visits", icon: ClipboardList, label: "All Entries", testId: "nav-all-entries" },
  { grouped: true, path: "/rep-calendar", icon: CalendarClock, label: "Rep Calendar", testId: "nav-rep-calendar" },
  { grouped: true, path: "/upcoming", icon: CalendarCheck, label: "Future Schedule", testId: "nav-future-schedule" },
];

const reportsItems = [
  { grouped: true, path: "/eod", icon: ClipboardCheck, label: "End of Day Reports", testId: "nav-end-of-day-reports" },
  { grouped: true, path: "/reports", icon: GitBranch, label: "Pipeline Reports", testId: "nav-pipeline-reports" },
  { grouped: true, path: "/insights", icon: BarChart3, label: "Insights", testId: "nav-insights" },
  { grouped: true, path: "/eod-snapshots", icon: CalendarCheck, label: "EOD Snapshots", testId: "nav-eod-snapshots" },
  { grouped: true, path: "/pipeline-reports", icon: TrendingUp, label: "Pipeline Analytics", testId: "nav-pipeline-analytics" },
  { grouped: true, path: "/doctor-timeline", icon: Stethoscope, label: "Doctor Timeline", testId: "nav-doctor-timeline" },
  { grouped: true, path: "/smart-reports", icon: Zap, label: "SmartReports", testId: "nav-smart-reports" },
];

const settingsItems = [
  { path: "/medical-aids", icon: Pill, label: "Medical Aids" },
  { path: "/exports", icon: FileSpreadsheet, label: "Exports" },
];

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard", managerOnly: true },
  { path: "/doctors", icon: Users, label: "Doctors", repVisible: true },
  { path: "/rep/territories", icon: Map, label: "Rep Territories", repVisible: true },
  { grouped: true, path: "/visits", icon: ClipboardList, label: "My Entries", repVisible: true },
  { grouped: true, path: "/rep-calendar", icon: CalendarClock, label: "Rep Calendar", repVisible: true },
  { grouped: true, path: "/upcoming", icon: CalendarCheck, label: "Future Schedule", repVisible: true },
  { grouped: true, path: "/reports", icon: BarChart3, label: "Reports", repVisible: true },
  { grouped: true, path: "/eod", icon: ClipboardCheck, label: "End of Day", managerOnly: true },
];

// Helper to check if user is manager or admin
const isManagerOrAdmin = (user) => {
  return user?.role === "manager" || user?.role === "admin";
};

// Helper to check if user is a rep
const isRep = (user) => {
  return user?.role === "rep";
};

// Helper to get role badge color
const getRoleBadgeColor = (role) => {
  switch (role) {
    case "admin": return "bg-red-100 text-red-800";
    case "manager": return "bg-purple-100 text-purple-800";
    default: return "bg-blue-100 text-blue-800";
  }
};

const Sidebar = ({ onClose, user }) => {
  const [openGroup, setOpenGroup] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const onPipelinePage = location.pathname === "/referrals";
  const pipelineType = searchParams.get("type") === "tt" ? "tt" : "ss";

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      toast.success("Logged out successfully");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  // Filter nav items based on role
  const filteredNavItems = navItems.filter(item => {
    // For reps: only show items with repVisible: true
    if (isRep(user)) {
      return item.repVisible === true;
    }
    // For managers/admins: grouped items live in the REPS / Reports dropdowns
    return !item.grouped;
  });

  // Update labels based on role
  const getNavLabel = (item) => {
    if (isManagerOrAdmin(user)) {
      // Show "All Entries" / "All Pipeline" for managers
      if (item.path === "/visits") return "All Entries";
      if (item.path === "/referrals") return "All Pipeline";
    }
    return item.label;
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="font-bold text-lg text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
            RolodexCRM
          </span>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon className="w-5 h-5" />
            <span>{getNavLabel(item)}</span>
          </NavLink>
        ))}

        {isManagerOrAdmin(user) && (
          <div className="pt-3 mt-3 border-t border-border space-y-1">
            {[
              { key: "pipeline", label: "All Pipeline", icon: GitBranch, items: pipelineItems, testId: "pipeline-toggle", menuTestId: "pipeline-submenu" },
              { key: "reps", label: "REPS", icon: Users, items: repsItems, testId: "reps-toggle", menuTestId: "reps-submenu" },
              { key: "reports", label: "Reports", icon: BarChart3, items: reportsItems, testId: "reports-toggle", menuTestId: "reports-submenu" },
              { key: "settings", label: "Settings", icon: Cog, items: settingsItems, testId: "settings-toggle", menuTestId: "settings-submenu" },
            ].map((group) => {
              const isOpen = openGroup === group.key;
              return (
                <div key={group.key}>
                  <button
                    onClick={() => setOpenGroup(isOpen ? null : group.key)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    data-testid={group.testId}
                  >
                    <group.icon className="w-5 h-5" />
                    <span>{group.label}</span>
                    {isOpen ? (
                      <ChevronUp className="w-4 h-4 ml-auto" />
                    ) : (
                      <ChevronDown className="w-4 h-4 ml-auto" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="mt-1 ml-5 space-y-1 border-l border-border pl-3" data-testid={group.menuTestId}>
                      {group.items.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          onClick={onClose}
                          className={({ isActive }) => {
                            const active = item.pipeline
                              ? onPipelinePage && pipelineType === item.pipeline
                              : isActive;
                            return `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                              active
                                ? "bg-primary/10 text-primary font-medium"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            }`;
                          }}
                          data-testid={item.testId || `nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className="w-4 h-4" />
                          <span>{item.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-border">
        {user && (
          <div className="flex items-center gap-3 mb-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-10 h-10 rounded-full"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-medium">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
              <div className="flex items-center gap-1">
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
              <Badge className={`text-[10px] mt-1 ${getRoleBadgeColor(user.role)}`}>
                {user.role || "rep"}
              </Badge>
            </div>
          </div>
        )}
        <div className="mb-3">
          <PresenceBar />
        </div>
        <Button
          variant="outline"
          className="w-full justify-start gap-2"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

const DARK_THEME_CSS = `
html.dark { color-scheme: dark; }
html.dark {
  --background: 222 22% 8%;
  --foreground: 210 20% 92%;
  --card: 222 20% 12%;
  --card-foreground: 210 20% 92%;
  --popover: 222 20% 12%;
  --popover-foreground: 210 20% 92%;
  --primary: 217 91% 60%;
  --primary-foreground: 222 47% 11%;
  --secondary: 217 19% 20%;
  --secondary-foreground: 210 20% 92%;
  --muted: 217 19% 18%;
  --muted-foreground: 215 16% 65%;
  --accent: 217 19% 22%;
  --accent-foreground: 210 20% 92%;
  --border: 217 19% 24%;
  --input: 217 19% 24%;
  --ring: 217 91% 60%;
}
html.dark body { background-color: hsl(222 22% 8%); color: hsl(210 20% 92%); }
html.dark .bg-white { background-color: hsl(222 20% 12%); }
html.dark .bg-gray-50, html.dark .bg-slate-50, html.dark .bg-gray-100, html.dark .bg-slate-100 { background-color: hsl(222 22% 9%); }
html.dark .hover\\:bg-gray-50:hover, html.dark .hover\\:bg-gray-100:hover, html.dark .hover\\:bg-slate-50:hover { background-color: hsl(217 19% 20%); }
html.dark .text-gray-900, html.dark .text-slate-900, html.dark .text-gray-800, html.dark .text-slate-800, html.dark .text-black { color: hsl(210 20% 92%); }
html.dark .text-gray-700, html.dark .text-slate-700, html.dark .text-gray-600, html.dark .text-slate-600 { color: hsl(215 16% 74%); }
html.dark .text-gray-500, html.dark .text-slate-500, html.dark .text-gray-400 { color: hsl(215 16% 62%); }
html.dark .border-gray-200, html.dark .border-gray-300, html.dark .border-slate-200, html.dark .border-slate-300 { border-color: hsl(217 19% 24%); }
html.dark input, html.dark select, html.dark textarea { background-color: hsl(222 20% 15%); color: hsl(210 20% 92%); }
html.dark table tbody tr:hover { background-color: hsl(217 19% 18%); }
`;

const ThemeToggle = ({ isDarkMode, onToggle, testId }) => (
  <button
    onClick={onToggle}
    className="p-2 rounded-md border border-border bg-background hover:bg-muted transition-colors"
    title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
    aria-label="Toggle colour theme"
    data-testid={testId}
  >
    {isDarkMode ? <Sun className="h-5 w-5 text-yellow-500" /> : <Moon className="h-5 w-5 text-gray-600" />}
  </button>
);

const Layout = ({ children, user }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("crm-theme-mode");
    return saved ? JSON.parse(saved) : false;
  });
  const location = useLocation();

  useEffect(() => {
    let styleEl = document.getElementById("crm-dark-theme");
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = "crm-dark-theme";
      styleEl.textContent = DARK_THEME_CSS;
      document.head.appendChild(styleEl);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("crm-theme-mode", JSON.stringify(isDarkMode));
    document.documentElement.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);
  
  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Get current page title for mobile header
  const getCurrentPageTitle = () => {
    const path = location.pathname;
    const item = [...navItems, ...repsItems, ...reportsItems, ...settingsItems].find(nav => path.startsWith(nav.path));
    if (path.startsWith('/cases/')) return 'Case Detail';
    return item?.label || 'RolodexCRM';
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? "dark bg-background" : "bg-slate-50"}`}>

      {/* Desktop theme toggle (top right) */}
      <div className="hidden lg:block fixed top-4 right-6 z-50">
        <ThemeToggle isDarkMode={isDarkMode} onToggle={() => setIsDarkMode((v) => !v)} testId="theme-toggle-button" />
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 w-64 h-full border-r border-border z-40">
        <Sidebar user={user} />
      </div>

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-border z-50 px-4 flex items-center justify-between shadow-sm">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <Sidebar onClose={() => setMobileOpen(false)} user={user} />
          </SheetContent>
        </Sheet>
        
        <div className="flex items-center gap-2">
          <span className="font-bold text-primary" style={{ fontFamily: 'Manrope, sans-serif' }}>
            RolodexCRM
          </span>
        </div>
        
        <div className="flex items-center gap-2">
        <ThemeToggle isDarkMode={isDarkMode} onToggle={() => setIsDarkMode((v) => !v)} testId="theme-toggle-button-mobile" />
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="w-8 h-8 rounded-full"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="text-primary text-xs font-medium">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
        )}
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 lg:pr-16">
        {React.cloneElement(children, { user })}
      </main>
    </div>
  );
};

export default Layout;
